import Storage from '@homeservices/tool-kit/storage/local-storage';

import {
  getMe,
} from '@homeservices/api-kit/security/users';
import {
  updateMe,
  createUser,
} from '@homeservices/api-kit/search/user';

import {
  getServiceProviders,
} from '@homeservices/api-kit/serviceprovider';

import { COOKIE_CORE_AUTH, COOKIE_AUTH } from '@homeservices/skeleton-key';
import { WARNINGS_STORAGE_NAME } from '@/store/modules/warnings';
import { JOB_REQUEST_LOCAL_STORAGE_NAME } from '@/store/modules/jobrequest';
import { eraseCookie } from '@/utils/cookies';
import { getPRWorkOrders } from '@homeservices/api-kit/workorder/proreferral';

const isDev = process.env.NODE_ENV === 'development';

function initialState () {
  return {
    me: {},
    isPro: false,
    isLoggedIn: false,
    isBlocked: false,
    canSubmitLeads: true,
  };
}

export default {
  namespaced: true,
  state: initialState,
  getters: {
    me ({ me }) {
      return me;
    },
    isLoggedIn ({ isLoggedIn }) {
      return isLoggedIn;
    },
    isPro ({ isPro }) {
      return isPro;
    },
    isBlocked ({ isBlocked }) {
      return isBlocked;
    },
    userId (state, { me }) {
      return me.id;
    },
    profileImage (state, { me }) {
      return me.avatar;
    },
    firstName (state, { me }) {
      return me.first_name;
    },
    lastName (state, { me }) {
      return me.last_name;
    },
    fullName (state, getters) {
      const { firstName, lastName } = getters;
      return lastName ? `${firstName} ${lastName}` : firstName;
    },
    canSubmitLeads (state) {
      return state.canSubmitLeads;
    },
  },
  actions: {
    async create ({ dispatch }, userData) {
      const { data } = await createUser(userData);
      await dispatch('loginUserWithAuthData', data.token);
    },
    async loginUserWithAuthData ({ dispatch }, data) {
      await dispatch('skeletonKey/loginUserWithAuthData', data, { root: true });
      await dispatch('status');
    },
    async login ({ commit, dispatch }, { username, password, type }) {
      await dispatch('skeletonKey/login', { username, password, type }, { root: true });
      await Promise.all([
        dispatch('fetchMe'),
        dispatch('fetchIsPro'),
      ]);
      commit('setIsLoggedIn', true);
    },
    async status ({ commit, dispatch, rootGetters }) {
      await dispatch('skeletonKey/status', null, { root: true });
      if (rootGetters['skeletonKey/isLoggedIn']) {
        await Promise.all([
          dispatch('fetchMe'),
          dispatch('fetchIsPro'),
        ]);
        commit('setIsLoggedIn', true);
      }
    },
    async fetchMe ({ commit, dispatch }) {
      try {
        const result = await getMe();
        commit('setMe', result.data);
        dispatch('checkNumberOfLeads');
      } catch (err) {
        if (isDev) {
          console.error('fetchMe silently failed: ', err); // eslint-disable-line no-console
        }
      }
    },
    async updateMe ({ dispatch }, userData) {
      await updateMe(userData);
      await dispatch('fetchMe');
    },
    async fetchIsPro ({ commit }) {
      try {
        const { data } = await getServiceProviders();
        commit('setIsPro', Boolean(data.results.length));
      } catch (err) {
        if (isDev) {
          console.error('fetchIsPro silently failed: ', err); // eslint-disable-line no-console
        }
        // TODO(hamzeh) - API returns error when called with a consumer based access token.
        // Confirm with core this is expected.
        commit('setIsPro', false);
      }
    },
    logout ({ dispatch, state }) {
      // Check if user is Pro before clearing session
      const { isPro } = state;
      dispatch('logoutAndClearUserSession');
      // Redirect Pro users to Pro homepage, consumers to regular homepage
      window.location.href = isPro ? '/pro-homepage' : '/';
    },
    async logoutAndClearUserSession ({ commit, dispatch }) {
      commit('clear');
      Storage.remove(WARNINGS_STORAGE_NAME);
      Storage.remove(JOB_REQUEST_LOCAL_STORAGE_NAME);
      await dispatch('logOutWithoutClearingSession');
    },
    logOutWithoutClearingSession ({ commit, dispatch }) {
      dispatch('skeletonKey/logout', null, { root: true });
      // TODO: Remove this when skeleton key is updated
      eraseCookie(COOKIE_AUTH);
      eraseCookie(COOKIE_CORE_AUTH);
      commit('setIsLoggedIn', false);
    },
    async checkNumberOfLeads ({ commit, rootGetters }) {
      const today = new Date().toISOString().split('T')[0];
      const params = {
        size: 3,
        start_date: today,
        end_date: today,
        sort: 'time_created desc',
      };
      const { data: { results } } = await getPRWorkOrders(params);
      commit('setCanSubmitLeads', !(results.length >= 3 && !rootGetters['loginspoof/isLDAPLoggedIn']));
    },
  },
  mutations: {
    setMe (state, value) {
      state.me = value;
    },
    setIsPro (state, value) {
      state.isPro = value;
    },
    setIsLoggedIn (state, value) {
      state.isLoggedIn = value;
    },
    setIsBlocked (state, value) {
      state.isBlocked = value;
    },
    clear (state) {
      Object.assign(state, initialState());
    },
    setCanSubmitLeads (state, value) {
      state.canSubmitLeads = value;
    },
  },
};
